import { useState } from 'react';
import { toast } from 'sonner';

export interface StripeError {
  message: string;
}

export const useStripePayment = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<StripeError | null>(null);

  const handlePayment = async (userId: string) => {
    if (!userId) {
      setError({ message: 'ID de usuario no válido' });
      throw new Error('ID de usuario no válido');
    }

    setIsLoading(true);
    setError(null);

    try {
      const workerUrl = import.meta.env.VITE_WORKER_URL;
      const isDevelopment = import.meta.env.MODE === 'development';
      
      if (!workerUrl) {
        throw new Error('URL del worker no configurada');
      }

      const response = await fetch(`${workerUrl}/api/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify({
          user_id: userId,
        }),
        credentials: 'omit', 
        mode: 'cors'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        console.error('Error en la respuesta:', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          error: errorData
        });
        throw new Error(errorData.error || 'Error en el proceso de pago');
      }

      const data = await response.json();
      
      if (!data.url) {
        throw new Error('No se recibió la URL del checkout');
      }

      window.location.href = data.url;
    } catch (error) {
      console.error('Error al procesar el pago:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido al procesar el pago';
      setError({ message: errorMessage });
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { handlePayment, isLoading, error };
};
